import React, { memo, useMemo, useRef, useEffect, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppStateContext } from '../context/AppContext';
import PhishingHeader from './PhishingHeader';
import Footer from './Footer';
import ErrorBoundary from './ErrorBoundary';
import { BaseComponentProps } from '../types/components';
import styles from './Layout.module.css';

/**
 * Error boundary props for Layout component
 */
interface LayoutErrorBoundaryProps {
  /** Custom error message to display */
  errorMessage?: string;
  /** Custom fallback render function */
  renderFallback?: (error: Error) => ReactNode;
  /** Error handler callback */
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

/**
 * Layout component props
 */
interface LayoutProps extends BaseComponentProps {
  /** Main content to render within the layout */
  children: ReactNode;
  /** ARIA label for the main content area */
  mainContentLabel?: string;
  /** Text for the skip navigation link */
  skipLinkText?: string;
  /** Props to pass to the ErrorBoundary component */
  errorBoundaryProps?: LayoutErrorBoundaryProps;
  /** Test ID for testing purposes */
  testId?: string;
}

/**
 * Layout Component
 *
 * A responsive and accessible layout wrapper that provides:
 * - Semantic HTML structure with proper ARIA landmarks
 * - Skip link for keyboard navigation accessibility
 * - Focus management on route changes
 * - High contrast and dark mode theme support
 * - Error boundary for robust error handling
 * - Performance optimization with React.memo
 * - Customizable content and styling
 * - Full TypeScript support with comprehensive prop validation
 */
const Layout = memo<LayoutProps>(
  ({
    children,
    mainContentLabel,
    skipLinkText = 'Skip to main content',
    className = '',
    errorBoundaryProps = {},
    testId = 'layout',
    'aria-label': ariaLabel,
    'data-testid': dataTestId,
    ...props
  }) => {
    const { highContrast } = useAppStateContext();
    const location = useLocation();
    const mainContentRef = useRef<HTMLElement>(null);

    // Focus management for route changes
    useEffect(() => {
      if (mainContentRef.current) {
        // Move focus to main content on route change
        mainContentRef.current.focus();
        // Scroll to top on route change
        mainContentRef.current.scrollIntoView({ block: 'start', behavior: 'smooth' });
}
}, [location.pathname]);

    // Memoize layout classes to prevent unnecessary re-renders
    const layoutClasses = useMemo(() => {
      const baseClasses = [(styles as any).layout];

      if (highContrast) {
        baseClasses.push((styles as any).layoutHighContrast);
  } else {
        baseClasses.push((styles as any).layoutDefault, (styles as any).layoutDark);
}

      if (className) {
        baseClasses.push(className);
}

      return baseClasses.join(' ');
}, [highContrast, className]);

    // Memoize error boundary configuration
    const errorBoundaryConfig = useMemo(
      () => ({
        errorMessage:
          'Something went wrong while loading the content. Please try again.',
        ...errorBoundaryProps,
  }),
      [errorBoundaryProps]
    );

    return (
      <div 
        className={layoutClasses} 
        data-testid={dataTestId || testId}
        aria-label={ariaLabel || undefined}
        {...props}
      >
        {/* Skip link for accessibility - visible only when focused */}
        <a
          href='#main-content'
          className={(styles as any).skipLink}
          data-testid='skip-link'
        >
          {skipLinkText}
        </a>

        {/* Header with navigation */}
        <PhishingHeader />

        {/* Main content area with error boundary */}
        <main
          ref={mainContentRef}
          id='main-content'
          className={(styles as any).mainContent}
          role='main'
          aria-label={mainContentLabel || 'Main content' || undefined}
          data-testid='main-content'
          tabIndex={-1}
        >
          <ErrorBoundary {...errorBoundaryConfig}>
            <div className={(styles as any).errorBoundaryWrapper}>{children}</div>
          </ErrorBoundary>
        </main>

        {/* Footer */}
        <Footer />
      </div>
    );
}
);

Layout.displayName = 'Layout';

export default Layout;
